import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { CustomSelect, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton } from '@redpanda/ui';
import { generateMonthlyPayrolls } from '../services/api';
import { usePayrollStoreBase } from '../stores/payrollStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { monthOptions, yearOptions } from '../utils/period';

interface Result {
  created: number;
  existing: string[];
  noSetting: string[];
}

/**
 * 產生某月全部員工的薪資草稿（一般企業的月結流程：產生草稿 → 逐筆調整獎金、扣款 → 確認 → 發放）
 * 依各員工的薪資設定與當月加班時數計算；已有薪資的略過、沒有薪資設定的列出姓名
 */
const GeneratePayrollDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation(['pages', 'translation']);
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const { data } = await generateMonthlyPayrolls(Number(year), Number(month));
      setResult(data);
      if (data.created > 0) {
        await usePayrollStoreBase.getState().refreshData();
        notifyDataChanged('payrolls');
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'translation:common.messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell onClose={onClose} closeDisabled={loading} className="max-w-md">
      <DialogHeader title={t('pages:payroll.generate.title')} onClose={onClose} closeDisabled={loading} closeLabel={t('translation:common.actions.close')} />
      {result ? (
        <>
          <div className="p-6 space-y-3 text-sm text-fg">
            <p className="font-medium">{t('pages:payroll.generate.created', { count: result.created, year, month })}</p>
            {result.existing.length > 0 && (
              <p className="text-fg-secondary">{t('pages:payroll.generate.existing', { count: result.existing.length })}</p>
            )}
            {result.noSetting.length > 0 && (
              <div>
                <p className="text-red-600 dark:text-red-400">{t('pages:payroll.generate.noSetting', { count: result.noSetting.length })}</p>
                <p className="mt-1 text-fg-secondary">{result.noSetting.join('、')}</p>
              </div>
            )}
            {result.created > 0 && <p className="text-fg-secondary">{t('pages:payroll.generate.next')}</p>}
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className={dialogButtonClass.primary}>{t('translation:common.actions.close')}</button>
          </DialogFooter>
        </>
      ) : (
        <form onSubmit={submit}>
          <div className="p-6 space-y-5">
            <p className="text-sm text-fg-secondary">{t('pages:payroll.generate.description')}</p>
            <div className="grid grid-cols-2 gap-4">
              <FormFieldWithError label={t('pages:payroll.form.year')} required htmlFor="generateYear">
                <CustomSelect id="generateYear" value={year} onChange={setYear} options={yearOptions(1, 1)} />
              </FormFieldWithError>
              <FormFieldWithError label={t('pages:payroll.form.month')} required htmlFor="generateMonth">
                <CustomSelect id="generateMonth" value={month} onChange={setMonth} options={monthOptions(t)} />
              </FormFieldWithError>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} disabled={loading} className={dialogButtonClass.cancel}>{t('translation:common.actions.cancel')}</button>
            <LoadingButton type="submit" loading={loading} className={dialogButtonClass.primary}>{t('pages:payroll.generate.submit')}</LoadingButton>
          </DialogFooter>
        </form>
      )}
    </DialogShell>
  );
};

export default GeneratePayrollDialog;
